import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { EmailTagInput } from '@/components/EmailTagInput';
import { InviteClientHeader } from '@/components/invite-client/InviteClientHeader';
import { InviteClientProgressBar } from '@/components/invite-client/InviteClientProgressBar';

import { ChevronRight, Mail } from 'lucide-react';

import { useInviteClientContext } from '@/components/invite-client/InviteClientContext';

import { cn } from '@client/lib';
import { ChevronLeft } from '@mui/icons-material';

export function InviteClientEmailInfo() {
  const {
    inviteClientConfig,
    isEmailValid,
    canContinue,
    handleStepChange,
    toggleBulkMode,
    handleConfigChange,
  } = useInviteClientContext();

  const { isBulkMode, bulkEmails, email, firstName, lastName } = inviteClientConfig;

  return (
    <>
      <InviteClientProgressBar
        currentStep={1}
        stepLabel="Client Info"
      />

      <InviteClientHeader
        title="Who are you inviting?"
        description="Add one or more clients to send invitations"
      />

      <div className="mt-4">
        {!isBulkMode ? (
          <div className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4">
            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="email"
                className="text-xs font-medium text-gray-700"
              >
                Email address <span className="text-red-700">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="client@example.com"
                value={email}
                onChange={({ target }) => handleConfigChange('email', target.value)}
                className={cn({
                  'border-red-500': email.length > 0 && !isEmailValid,
                })}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label
                  htmlFor="firstName"
                  className="text-xs font-medium text-gray-700"
                >
                  First name (optional)
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={({ target }) => handleConfigChange('firstName', target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label
                  htmlFor="lastName"
                  className="text-xs font-medium text-gray-700"
                >
                  Last name (optional)
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={({ target }) => handleConfigChange('lastName', target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={toggleBulkMode}
                className="hover:text-primary flex items-center gap-1 text-sm font-medium text-gray-700"
              >
                <Mail className="size-4" />
                Invite multiple clients
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <EmailTagInput
              onChange={value => handleConfigChange('bulkEmails', value)}
              initialEmails={bulkEmails}
              maxEmails={35}
            />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={toggleBulkMode}
                className="hover:text-primary flex items-center gap-1 text-sm font-medium text-gray-700"
              >
                <Mail className="size-4" />
                Invite single client
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => handleStepChange('method')}
        >
          <ChevronLeft className="size-4" />
          Back
        </Button>

        <div className="flex items-center gap-2">
          {isBulkMode && (
            <Button
              variant="ghost"
              className="bg-gray-100 text-gray-950"
              onClick={() => handleConfigChange('bulkEmails', [])}
            >
              Clear
            </Button>
          )}
          <Button
            disabled={!canContinue}
            onClick={() => handleStepChange('customize-email')}
          >
            Continue
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </>
  );
}
